export const API_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "") || (location.hostname === "localhost" ? "http://localhost:8787" : "https://directoryz-api.cnames-dev.workers.dev");

export type Directory = {
  id: string;
  name: string;
  slug: string;
  description: string;
  accent: string;
  background: string;
  layout: "grid" | "list";
  status: "draft" | "published";
  logo_key: string | null;
  logo_url: string | null;
  seo_title: string;
  seo_description: string;
  website_url: string;
  views: number;
  item_count?: number;
  public_url: string;
  updated_at: string;
};

export type DirectoryItem = {
  id: string;
  name: string;
  slug: string;
  short_description: string;
  description: string;
  website_url: string;
  image_key: string | null;
  image_url: string | null;
  icon_url: string;
  category: string;
  location: string;
  tags: string[];
  featured: boolean;
  status: "draft" | "published";
  views: number;
  updated_at: string;
};

export type ItemInput = Omit<DirectoryItem, "id" | "slug" | "image_url" | "views" | "updated_at"> & { slug?: string };

export type ItemEnrichment = {
  name: string;
  short_description: string;
  description: string;
  website_url: string;
  icon_url: string;
  image_key: string | null;
  image_url: string | null;
  category: string;
  location: string;
  tags: string[];
  warnings: string[];
};

export type AccountUser = {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  emailVerified: boolean;
  plan: "free" | "pro" | "team";
  billingProvider: "stripe" | "appsumo" | null;
  subscriptionStatus: string | null;
  subscriptionInterval: string | null;
  currentPeriodEnd: number | null;
  appsumoLicenseKey: string | null;
};

const TOKEN_KEY = "directoryz_session";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (options.body && !(options.body instanceof FormData)) headers.set("Content-Type", "application/json");
  const response = await fetch(`${API_URL}${path}`, { ...options, headers });
  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: "Request failed" })) as { error?: string };
    if (response.status === 401) clearToken();
    throw new Error(data.error || "Request failed");
  }
  return await response.json() as T;
}

export function jsonBody(value: unknown): Pick<RequestInit, "body"> {
  return { body: JSON.stringify(value) };
}
